<template>
  <template v-if="userIsDisqualified">
    <CapacityOverview
      :title="this.$t('paymentsPage.disqualifiedMessage')"
      :cta-visible="isCtaVisible"
      :cta-label="getLabelForCta"
      :cta-click="handleCtaClick"
      :connected-accounts-visible="hasActiveSalesAccounts || userIsDisqualified"
      :connected-accounts-title="connectedAccountsTitle"
      :connected-accounts-show-error="showError"
      :connected-accounts-common-tracking-fields="commonTrackingFields"
      help-paragraph="paymentsPage.getHelpText"
      help-link="mailto:support@clear.co"
      help-link-text="support@clear.co"
    />
  </template>

  <template v-else-if="showSelfReported && !selfReportedCapacityExceeded">
    <CapacityOverview
      title="Funding Capacity"
      title-tag="Self-Reported"
      :cta-visible="isCtaVisible"
      :cta-label="getLabelForCta"
      :cta-click="handleCtaClick"
      :description="
        this.$t('paymentsPage.selfReportedRevCapacityWithoutNumber')
      "
      :available-capacity="remainingSelfReportedCapacityCents"
      :used-capacity="
        selfReportedCapacityCents - remainingSelfReportedCapacityCents
      "
      :total-capacity="selfReportedCapacityCents"
      :track-education-link="trackEducationLink"
      :connected-accounts-visible="hasActiveSalesAccounts || userIsDisqualified"
      :connected-accounts-title="connectedAccountsTitle"
      :connected-accounts-show-error="showError"
      :connected-accounts-common-tracking-fields="commonTrackingFields"
      :capacity-calculating="capacityCalculating"
      :disclaimer="DISCLAIMERS.VERIFYING_SALES_ACCOUNTS"
    />
  </template>

  <template v-else-if="showSelfReported && selfReportedCapacityExceeded">
    <CapacityOverview
      title="You have exceeded your Self Reported Capacity"
      :cta-visible="isCtaVisible"
      :cta-label="getLabelForCta"
      :cta-click="handleCtaClick"
      :track-education-link="trackEducationLink"
      :connected-accounts-visible="hasActiveSalesAccounts || userIsDisqualified"
      :connected-accounts-title="connectedAccountsTitle"
      :connected-accounts-show-error="showError"
      :connected-accounts-common-tracking-fields="commonTrackingFields"
      help-paragraph="paymentsPage.maxedOutCapacityHelp"
      :help-link="contactSupportMethods.email.href"
      :help-link-text="contactSupportMethods.email.display"
    >
      <template v-slot:custom-description>
        <p>
          You have exceeded your Self Reported Funding Capacity of
          <strong>{{ toAmountNoDecimal(selfReportedCapacityCents) }}</strong
          >. To avoid funding delays, connect additional sales accounts to
          increase Funding Capacity or cancel and resubmit based on your Self
          Reported Funding Capacity.
        </p>
      </template>
    </CapacityOverview>
  </template>

  <template v-else-if="showPlaidDisconnectedText">
    <CapacityOverview
      title="Bank accounts not connected"
      :cta-visible="isCtaVisible"
      :cta-label="getLabelForCta"
      :cta-click="handleCtaClick"
      :track-education-link="trackEducationLink"
      :connected-accounts-visible="hasActiveSalesAccounts || userIsDisqualified"
      :connected-accounts-title="connectedAccountsTitle"
      :connected-accounts-show-error="showError"
      :connected-accounts-common-tracking-fields="commonTrackingFields"
    >
      <template v-slot:custom-description>
        To view your Funding Capacity,
        <router-link :to="{ name: RouteNames.BANKING.INDEX }">
          connect with Plaid
        </router-link>
        to link your bank accounts or contact us at
        <a href="mailto:support@clear.co">support@clear.co</a>
      </template>
    </CapacityOverview>
  </template>

  <template v-else-if="zeroCapacity">
    <CapacityOverview
      :title="$t('paymentsPage.zeroCapacity')"
      :connected-accounts-visible="hasActiveSalesAccounts || userIsDisqualified"
      :connected-accounts-title="connectedAccountsTitle"
      :connected-accounts-show-error="showError"
      :connected-accounts-common-tracking-fields="commonTrackingFields"
      help-paragraph="paymentsPage.zeroCapacityHelp"
      :help-link="contactSupportMethods.email.href"
      :help-link-text="contactSupportMethods.email.display"
    />
  </template>

  <template v-else-if="!capacityIsApproved && maxCapacityExceeded">
    <CapacityOverview
      title="You have exceeded your Preliminary Funding Capacity"
      :cta-visible="isCtaVisible"
      :cta-label="getLabelForCta"
      :cta-click="handleCtaClick"
      :track-education-link="trackEducationLink"
      :connected-accounts-visible="hasActiveSalesAccounts || userIsDisqualified"
      :connected-accounts-title="connectedAccountsTitle"
      :connected-accounts-show-error="showError"
      :connected-accounts-common-tracking-fields="commonTrackingFields"
      help-paragraph="paymentsPage.maxedOutCapacityHelp"
      :help-link="contactSupportMethods.email.href"
      :help-link-text="contactSupportMethods.email.display"
    >
      <template v-slot:custom-description>
        <p>
          You have exceeded your Preliminary Funding Capacity of
          <strong>{{ toAmountNoDecimal(businessCapacity.totalCents) }}</strong
          >. To avoid funding delays, connect additional sales accounts to
          increase Funding Capacity or cancel and resubmit based on your
          Preliminary Funding Capacity.
        </p>
      </template>
    </CapacityOverview>
  </template>

  <template v-else-if="maxedOutCapacity">
    <CapacityOverview
      :title="$t('paymentsPage.maxedOutCapacity')"
      :connected-accounts-visible="hasActiveSalesAccounts || userIsDisqualified"
      :connected-accounts-title="connectedAccountsTitle"
      :connected-accounts-show-error="showError"
      :connected-accounts-common-tracking-fields="commonTrackingFields"
      help-paragraph="paymentsPage.getHelpText"
      :help-link="contactSupportMethods.email.href"
      :help-link-text="contactSupportMethods.email.display"
    />
  </template>

  <template v-else-if="showPreliminaryCapacity">
    <CapacityOverview
      title="Fixed Funding Capacity"
      title-tag="Preliminary"
      :cta-visible="isCtaVisible"
      :cta-label="getLabelForCta"
      :cta-click="handleCtaClick"
      description="Based on connected accounts revenue. Submit a request to get Approved Funding Capacity."
      :available-capacity="businessCapacity?.availableCents"
      :used-capacity="businessCapacity?.usedCents"
      :total-capacity="businessCapacity?.totalCents"
      :track-education-link="trackEducationLink"
      :connected-accounts-visible="hasActiveSalesAccounts || userIsDisqualified"
      :connected-accounts-title="connectedAccountsTitle"
      :connected-accounts-show-error="showError"
      :connected-accounts-common-tracking-fields="commonTrackingFields"
      :disclaimer="DISCLAIMERS.PRELIMINARY_CAPACITY"
    />
  </template>

  <template v-else>
    <CapacityOverview
      :title="`${capacityType} Capacity`"
      title-tag="Approved"
      :cta-visible="isCtaVisible"
      :cta-label="getLabelForCta"
      :cta-click="handleCtaClick"
      :description="`${isApprovedRollingCapacity ? 'Rolling Capacity approved' : 'Approved'} as of ${approvedDate}.`"
      :description-renewal-in-days="
        daysTillCapacityRenew > 0 ? daysTillCapacityRenew : 1
      "
      :description-tooltip="`Upon ${capacityType} Capacity refresh, your maximum ${capacityType} Capacity may change based on your connected revenue accounts. Approved Capacity is also subject to change based on Clearco’s ongoing diligence requirements.`"
      :available-capacity="businessCapacity?.availableCents"
      :used-capacity="businessCapacity?.usedCents"
      :total-capacity="businessCapacity?.totalCents"
      :track-education-link="trackEducationLink"
      :connected-accounts-visible="false"
      :connected-accounts-title="connectedAccountsTitle"
      :connected-accounts-show-error="showError"
      :connected-accounts-common-tracking-fields="commonTrackingFields"
      :capacity-calculating="capacityCalculating"
      :disclaimer="DISCLAIMERS.APPROVED_CAPACITY"
    />
  </template>
</template>
<script>
import { mapGetters } from 'vuex';
import { mapRequestStatuses } from '@/utils/vuex-api-utils';
import { V2_CAPACITY_ERROR_TYPES } from '@/data/payments';
import { DISCLAIMERS } from '@/data/capacity';
import { RouteNames } from '@/data/routes';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';
import {
  differenceInDays,
  startOfToday,
  startOfDay,
  parseISO,
  parse,
  format,
} from 'date-fns';
import { ContactSupportMethods } from '@/data/contact-support';
import CapacityOverview from '@/components/CapacityOverview.vue';

const CAPACITY_BOTTOM_OUT_CENTS = 100000; // $1000.00

export default {
  components: {
    CapacityOverview,
  },
  data() {
    return {
      showError: false,
      contactSupportMethods: ContactSupportMethods,
      DISCLAIMERS,
      RouteNames,
    };
  },
  props: {
    showSelfReported: { type: Boolean },
    errorType: { type: String },
  },
  computed: {
    ...mapGetters([
      'hasActiveSalesAccounts',
      'isBnplWaitlisted',
      'userCanSubmitInvoices',
      'productSegmentLabel',
      'isPrimaryBankAccountDisconnected',
      'businessOnPlaidBypass',
      'isBusinessProfileComplete',
      'business',
      'businessCapacity',
      'selfReportedCapacityCents',
      'maxCapacityExceeded',
      'businessCapacityGenerated',
      'remainingSelfReportedCapacityCents',
      'userIsDisqualified',
      'isApprovedRollingCapacity',
      'capacityType',
      'zeroCapacity',
    ]),
    ...mapRequestStatuses({
      getCapacityRequest: 'GET_BUSINESS_CAPACITY',
    }),
    daysTillCapacityRenew() {
      if (!this.businessCapacity?.expiresAt) {
        return 0;
      }

      return differenceInDays(
        startOfDay(parseISO(this.businessCapacity?.expiresAt)),
        startOfToday(),
      );
    },
    showPreliminaryCapacity() {
      return this.businessCapacity && !this.businessCapacity.approvedAt;
    },
    connectedAccountsTitle() {
      if (this.userIsDisqualified) {
        return this.$t('paymentsPage.disqualifiedTryToQuailify');
      }
      return this.$t('paymentsPage.calculatingCapacityConnectAccounts');
    },
    capacityCalculating() {
      return (
        this.errorType === V2_CAPACITY_ERROR_TYPES.CAPACITY_NOT_FOUND &&
        !this.businessCapacity?.totalCents &&
        this.hasActiveSalesAccounts
      );
    },
    showPlaidDisconnectedText() {
      return (
        this.isPrimaryBankAccountDisconnected &&
        this.getCapacityRequest.isSuccess &&
        !this.businessOnPlaidBypass
      );
    },
    maxedOutCapacity() {
      return (
        !this.zeroCapacity &&
        ((this.businessCapacity.availableCents <= CAPACITY_BOTTOM_OUT_CENTS &&
          !this.errorType) ||
          (!this.businessCapacityGenerated &&
            this.remainingSelfReportedCapacityCents <= 0))
      );
    },
    commonTrackingFields() {
      return {
        productSegment: this.productSegmentLabel,
        targetArea: this.$route.name,
      };
    },
    submitInvoiceCtaLabel() {
      return this.$t('paymentsPage.accessWorkingCapital');
    },
    waitlistCtaLabel() {
      return this.$t('account.pages.pageVendors.joinTheWaitlist');
    },
    getLabelForCta() {
      if (this.isBnplWaitlisted) {
        return `${this.waitlistCtaLabel}`;
      }
      if (this.userCanSubmitInvoices) {
        return `${this.submitInvoiceCtaLabel}`;
      }
      return 'Finish your application';
    },
    isCtaVisible() {
      return !!(
        this.isBnplWaitlisted ||
        this.userCanSubmitInvoices ||
        !this.isBusinessProfileComplete
      );
    },
    approvedDate() {
      return format(
        parse(
          this.businessCapacity.approvedAt.split('T')[0].replaceAll('-', '/'),
          'yyyy/MM/dd',
          new Date(),
        ),
        'MMMM dd, yyyy',
      );
    },
    capacityIsApproved() {
      return this.businessCapacity?.approvedAt;
    },
    selfReportedCapacityExceeded() {
      return (
        this.showSelfReported && this.remainingSelfReportedCapacityCents <= 0
      );
    },
  },
  methods: {
    toAmountNoDecimal(amountCents, currencyCode) {
      return this.$filters.noDecimal(
        this.$filters.currency(amountCents, currencyCode),
        currencyCode,
      );
    },
    trackEducationLink() {
      let userState;

      if (this.disconnectedAccountsError) {
        userState = 'account-disconnected';
      } else if (this.capacityCalculating) {
        userState = 'capacity-generating';
      } else if (this.maxedOutCapacity) {
        userState = 'capacity-consumed';
      } else if (this.showSelfReported) {
        userState = 'self-reported-capacity';
      } else {
        userState = 'capacity';
      }

      analytics.track(SEGMENT_EVENTS.CLICKED_CAPACITY_EDUCATION, {
        state: userState,
      });
    },
    handleCtaClick() {
      if (this.isBnplWaitlisted) {
        analytics.track(SEGMENT_EVENTS.bnpl_waitlist_cta_clicked, {
          businessId: this.business.id,
          businessName: this.business.name,
        });
        this.$emit('joinWaitlist');
      } else if (this.userCanSubmitInvoices) {
        this.$emit('openPayInvoice');
      } else {
        this.$emit('finishApplicationCtaClicked');
      }
    },
  },
};
</script>
